// -----------------------------------------------------------------------------
// Library
// -----------------------------------------------------------------------------
import React, { useCallback, useState, useEffect } from "react";
import { Form, Switch, Tooltip, notification, Modal, Row, Col, TimePicker, Spin } from "antd";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router";
import axios from "axios";
import { EditOutlined, UploadOutlined } from "@ant-design/icons";
// -----------------------------------------------------------------------------
// Store
// -----------------------------------------------------------------------------
import { selectAssetUploadId, selectSelectedWorkout } from "store/selectors";
import { uploadAssetToMux, editWorkout } from "store/slices";
import {
	useDeleteWorkoutMutation,
	useEditScheduledStreamMutation,
	useEditWorkoutMutation,
	useGetCategoriesQuery,
	useGetDomainGymQuery,
	useGetGymWorkoutsQuery,
	useGetScheduledStreamsQuery,
	useGetMuxAuthenticatedUrlMutation,
	useGetS3SignedUrlMutation,
	useGetCoachesQuery,
	useGetGymEquipmentQuery,
	useAddGymEquipmentMutation,
	useCreateCoachMutation,
} from "store/api/api";

// -----------------------------------------------------------------------------
// Components
// -----------------------------------------------------------------------------
import { Button } from "components/Common/Button";
import { Button as AntButton, Typography, Flex, Collapse } from "antd";
import { Span4 } from "themes/default/_typography";
import { FormInput } from "components/Common/Form/FormInput";
import { FormTextArea } from "components/Common/Form/FormTextArea";
import { FormErrorTag } from "components/Common/Form/FormErrorTag";
import { FormTagSelect } from "components/Common/Form/FormTagSelect";
import { GenreSelector } from "components/Common/GenreSelector";
import ImageUpload from "components/ImageUpload";
import { FormSelect } from "components/Common/Form/FormSelect";
import { FormCheckbox } from "components/Common/Form/FormCheckbox";
import { validateRequiredEncodedLength, validateEncodedLength } from "helpers/Form/commonFormValidations";
import DateTimeTimezone from "components/DateTimeTimezone";
import VideoPlayer from "components/UI/VideoPlayer/VideoPlayer";
import MuxUploadAndVideoPlayer from "components/Forms/Workout/UploadAssetsForm.jsx/MuxUploadAndPlayer";
import alpha from "helpers/alpha";
import { CopyOutlined, DownOutlined } from "@ant-design/icons";

// -----------------------------------------------------------------------------
// Utils and Styles
// -----------------------------------------------------------------------------
import { getVideoMetadata } from "helpers/generic";
import { useRecoilValue } from "recoil";
import { userAtom } from "globalAtoms";
import ajaxRoutes from "utils/ajaxRoutes";
import Ajax from "utils/api";
import {
	maxImageAssetSize,
	imageSizeLimitError,
	defaultTimezone,
	durationOptions,
	workoutTypes,
	workoutStatuses,
} from "helpers/constants";
import { decodeString, encodeString, sortByProperty, validEquipments, validTrainers } from "utils";
import usePrimaryColor from "helpers/hooks/usePrimaryColor";
import "./styles.scss";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
dayjs.extend(utc);
dayjs.extend(timezone);
// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------
const convertTimeToSeconds = (time) => {
	const [hours, minutes, seconds] = time.split(":").map(Number);
	return hours * 3600 + minutes * 60 + (seconds || 0);
};
const EditWorkoutForm = ({ liveStream, handleCloseModal, uploading, setUploading }) => {
	const dispatch = useDispatch();
	const uploaderRef = React.useRef(null);
	const primaryColor = usePrimaryColor();
	const user = useRecoilValue(userAtom);
	const [videoFile, setVideoFile] = useState(null);
	const [thumbnailFile, setThumbnailFile] = useState(null);
	const [selectedGenre, setSelectedGenre] = useState({});
	const [muxDirectUploadUrl, setMuxDirectUploadUrl] = useState(null);
	const [muxDirectUploadId, setMuxDirectUploadId] = useState(null);
	const [addingNewVideo, setAddingNewVideo] = useState(false);
	const [isScheduledWorkout, setIsScheduledWorkout] = useState(false);
	const uploadId = useSelector(selectAssetUploadId);
	const selectedWorkout = useSelector(selectSelectedWorkout);
	const [signedMuxPreviewUrl, setSignedMuxPreviewUrl] = useState(null);
	const { gym } = useParams();
	const domainGymResponse = useGetDomainGymQuery(gym);
	const domainGymId = domainGymResponse.data?.id;
	const isPartnerGym = !!domainGymResponse.data?.is_partner_gym;
	const [modal, contextHolder] = Modal.useModal();
	const { refetch: refetchWorkouts } = useGetGymWorkoutsQuery(
		{ gym_id: domainGymId },
		{ skip: !domainGymResponse.data }
	);

	const { data: categoriesData, refetch: refetchCategories } = useGetCategoriesQuery(domainGymId);
	const [editWorkoutTrigger] = useEditWorkoutMutation();
	const [editWorkoutSaveTrigger] = useEditWorkoutMutation();
	const [deleteWorkoutTrigger] = useDeleteWorkoutMutation();
	const [editScheduledStreamTrigger] = useEditScheduledStreamMutation();
	const { refetch: refetchStreams, data } = useGetScheduledStreamsQuery(domainGymId, { skip: !domainGymResponse.data });
	const [createEquipmentTrigger] = useAddGymEquipmentMutation();
	const [createCoachTrigger] = useCreateCoachMutation();
	const {
		data: coachesData,
		isLoading: isCoachesLoading,
		refetch: refetchTrainers,
	} = useGetCoachesQuery(domainGymId, {
		skip: !domainGymId,
	});
	const { data: equipments, refetch: refetchEquipments } = useGetGymEquipmentQuery(domainGymId, {
		skip: !domainGymId,
	});

	const [cancellingScheduledRelease, setCancellingScheduledRelease] = useState(false);
	const [getMuxSignedUrlTrigger, { isLoading }] = useGetMuxAuthenticatedUrlMutation();
	const [form] = Form.useForm();
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [currentDatetime, setCurrentDatetime] = useState(null);
	const [getS3SignedUrlTrigger] = useGetS3SignedUrlMutation();
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState();
	const [changeThumbNail, setChangeThumbnail] = useState(false);

	const isEditingLive =
		selectedWorkout?.type === workoutTypes.SCHEDULED_LIVE_STREAM ||
		selectedWorkout?.type === workoutTypes.CURRENTLY_LIVE;

	const isPreRecorded = selectedWorkout?.type === workoutTypes.SCHEDULED_PRE_RECORDED;
	const editWorkoutData = async (workoutData) => {
		try {
			if (workoutData.duration && uploadId) {
				workoutData.upload_id = uploadId;
			}
			await editWorkoutSaveTrigger({ workout_id: selectedWorkout.id, ...workoutData }).unwrap();
			notification.success({
				message: "Workout Updated",
			});
			await refetchCategories();
			dispatch(
				editWorkout({
					workout_id: selectedWorkout.id,
					...workoutData,
				})
			);
			if (isEditingLive || isPreRecorded) {
				refetchStreams();
			}
			handleCloseModal();
		} catch (error) {
			notification.error({
				message: "An error occurred while updating workout",
			});
		}
	};

	useEffect(() => {
		setSelectedGenre({ id: parseInt(selectedWorkout?.playlist_id) || undefined, title: selectedWorkout?.music_genre });
	}, [selectedWorkout?.music_genre, selectedWorkout?.playlist_id]);

	useEffect(() => {
		if (selectedWorkout && !isEditingLive && selectedWorkout.type === workoutTypes.SCHEDULED_ON_DEMAND) {
			setIsScheduledWorkout(true);
		}
	}, [selectedWorkout, isEditingLive]);

	const active = selectedWorkout?.status === workoutStatuses.ACTIVE;
	const isDraft = selectedWorkout?.status === workoutStatuses.DRAFT;
	// TODO (koechkevin) Fix this crap
	const onFinish = async ({
		workoutCoach,
		workoutDescription,
		workoutShortDescription,
		workoutTitle,
		workoutCategories,
		workoutEquipment,
		workoutIsFree,
		workoutPreviouslyLive,
		workoutDate,
		workoutTime,
		workoutTimezone,
		workoutDuration,
		workoutStatus: status,
		asset_start_time,
		asset_end_time,
	}) => {
		if (loading || uploading || error || changeThumbNail) {
			return null;
		}
		const workoutIsFreeInt = workoutIsFree ? 1 : 0;
		const workoutPreviouslyLiveInt = workoutPreviouslyLive ? 1 : 0;
		const isLive = isEditingLive || isPreRecorded || selectedWorkout.type === workoutTypes.SCHEDULED_LIVE_STREAM;
		const dataToUpdate = {
			user_id: user?.id,
			status: addingNewVideo && !isLive ? workoutStatuses.DRAFT : status,
		};
		setLoading(true);

		if (videoFile && videoFile !== selectedWorkout.id) {
			try {
				const { duration } = await getVideoMetadata(videoFile.original);
				dispatch(uploadAssetToMux({ file: videoFile }));

				dataToUpdate.duration = duration;
			} catch (error) {
				console.error("error uploading video to mux", error);
			}
		}

		if (asset_start_time && asset_end_time) {
			dataToUpdate.asset_start_time = convertTimeToSeconds(asset_start_time.format("HH:mm:ss"));
			dataToUpdate.asset_end_time = convertTimeToSeconds(asset_end_time.format("HH:mm:ss"));
		}

		if (thumbnailFile && thumbnailFile !== selectedWorkout.id) {
			try {
				const s3KeyThumbnail = await uploadImageToS3();
				dataToUpdate.image_url = s3KeyThumbnail;
			} catch (error) {
				console.error("error uploading thumbnail to s3", error);
			}
		}

		if (workoutCoach) {
			const coaches = await validTrainers({
				domainGymId,
				coachesData,
				workoutCoach,
				createCoachTrigger,
			});
			dataToUpdate.coaches = coaches;
		}

		if (workoutIsFreeInt !== selectedWorkout.is_free && isPartnerGym) {
			dataToUpdate.is_free = workoutIsFreeInt;
		}

		if (workoutPreviouslyLiveInt !== selectedWorkout.is_previously_live && isPartnerGym) {
			dataToUpdate.is_previously_live = workoutPreviouslyLiveInt;
		}

		if (workoutDescription && workoutDescription !== selectedWorkout.description) {
			dataToUpdate.description = encodeString(workoutDescription);
		}

		if (workoutShortDescription && workoutShortDescription !== selectedWorkout.short_description) {
			dataToUpdate.short_description = encodeString(workoutShortDescription);
		}

		if (workoutTitle && workoutTitle !== selectedWorkout.name) {
			dataToUpdate.name = encodeString(workoutTitle);
		}

		if (workoutCategories) {
			const stringCategories = workoutCategories?.filter(Boolean)?.join(",")?.trim();
			const selectedWorkoutCategories = selectedWorkout.categories.map((c) => c.name).join(",");

			if (stringCategories !== selectedWorkoutCategories) {
				dataToUpdate.categories = stringCategories;
			}
		}

		const equipmentsList = await validEquipments({
			domainGymId,
			equipmentData: equipments,
			selectedEquipments: workoutEquipment,
			createEquipmentTrigger,
		});

		dataToUpdate.equipment_list = equipmentsList;

		if (selectedWorkout.playlist_id?.toString() !== (selectedGenre.id?.toString() ?? null)) {
			dataToUpdate.music_genre = selectedGenre.title || null;
			dataToUpdate.playlist_id = selectedGenre.id || null;
		}

		if (workoutDate && workoutTime && workoutTimezone && (isEditingLive || isPreRecorded)) {
			const day = dayjs(workoutDate).startOf("day").format("MM/DD/YYYY");
			const dayTime = dayjs(day + " " + workoutTime);
			const iso = dayTime.format("YYYY-MM-DDTHH:mm:ss");
			await editScheduledStreamTrigger({
				workout_id: selectedWorkout.id,
				gym_id: domainGymId,
				start_time: iso,
				time_zone: workoutTimezone,
				currently_streaming: false,
			}).unwrap();
		}

		if (workoutDate && workoutTime && workoutTimezone && selectedWorkout.start_time) {
			const day = dayjs(workoutDate).startOf("day").format("MM/DD/YYYY");
			const dayTime = dayjs(day + " " + workoutTime);
			const iso = dayTime.format("YYYY-MM-DDTHH:mm:ss");
			dataToUpdate.start_time = iso;
			dataToUpdate.time_zone = workoutTimezone;
		}

		if (workoutDate && workoutTime && workoutTimezone && isScheduledWorkout && !cancellingScheduledRelease && !active) {
			const day = dayjs(workoutDate).startOf("day").format("MM/DD/YYYY");
			const dayTime = dayjs(day + " " + workoutTime, "MM/DD/YYYY HH:mm");
			const iso = dayTime.format("YYYY-MM-DDTHH:mm:ss");
			dataToUpdate.is_scheduled = 1;
			dataToUpdate.type = workoutTypes.SCHEDULED_ON_DEMAND;
			dataToUpdate.start_time = iso;
			dataToUpdate.time_zone = workoutTimezone;
		}

		if (selectedWorkout.start_time && cancellingScheduledRelease) {
			// https://fitradio.slack.com/archives/C076CBMSRP0/p1741897577966499
			dataToUpdate.is_scheduled = 0;
			dataToUpdate.type = workoutTypes.ON_DEMAND;
			dataToUpdate.start_time = null;
			dataToUpdate.time_zone = null;
		}

		if (workoutDuration && workoutDuration !== selectedWorkout.duration) {
			dataToUpdate.duration = workoutDuration;
		}

		if (Object.keys(dataToUpdate).length || addingNewVideo) {
			dataToUpdate.gym_id = domainGymId;
			if (addingNewVideo && !isLive) {
				dataToUpdate.status = workoutStatuses.DRAFT;
				dataToUpdate.upload_id = null;
			}
			await editWorkoutData(dataToUpdate);
		} else {
			handleCloseModal();
		}
		//Check if only streaming time has changes, and if so, close Modal and refetch streams
		if ((isEditingLive || isPreRecorded) && workoutDate && workoutTime && workoutTimezone) {
			handleCloseModal();
			refetchStreams();
		}
		const promises = [refetchWorkouts(), refetchTrainers(), refetchEquipments()];
		await Promise.all(promises);
		setLoading(false);
	};

	const uploadAsset = async (response, thumbnailFile) => {
		const formData = new FormData();

		const headers = {
			"Content-Type": "multipart/form-data",
		};

		for (const field in response.fields) {
			formData.append(field, response.fields[field]);
		}

		formData.append("file", thumbnailFile);

		try {
			await axios.post(response.url, formData, { headers });
		} catch (error) {
			notification.error({
				message: "Error Uploading Image",
				description: "Please try again",
			});
		}
	};
	const uploadImageToS3 = async () => {
		const mimeMap = {
			"image/jpeg": "jpg",
			"image/png": "png",
		};
		let fileType = mimeMap[thumbnailFile.type];
		const s3KeyThumbnail = `assets/${domainGymId}/thumbnail-${new Date().getTime()}.${fileType}`;
		const s3SignedResponse = await getS3SignedUrlTrigger({ key: s3KeyThumbnail, type: "image" });
		await uploadAsset(s3SignedResponse.data.response, thumbnailFile);
		const fullS3ThumbNail = `${s3SignedResponse.data.response.url}/${s3KeyThumbnail}`;
		return fullS3ThumbNail;
	};

	const onApply = useCallback(() => {
		if (!videoFile) {
			setVideoFile(null);
		}
		if (!thumbnailFile) {
			setThumbnailFile(null);
		}
	}, [videoFile, thumbnailFile]);

	const onSaveThumbnail = useCallback((file) => {
		const blobToFile = new File([file], "workoutThumbnail.png", {
			type: "image/png",
		});
		setThumbnailFile(blobToFile);
		setChangeThumbnail(false);
	}, []);

	const onDeleteWorkout = async () => {
		const { error, data } = await deleteWorkoutTrigger({
			workout_id: selectedWorkout.id,
			gym_id: selectedWorkout.gym_id,
		});
		setConfirmOpen(false);
		if (data) {
			await Promise.all([refetchWorkouts(), refetchCategories()]);
			notification.success({ message: "Workout deleted successfully" });
			setTimeout(() => {
				handleCloseModal();
			}, 1000);
		}
		if (error) {
			notification.success({ message: "An error occurred while deleting a workout. Please try again" });
		}
	};

	const onCancelScheduledReleased = async () => {
		setCancellingScheduledRelease(true);
		setIsScheduledWorkout(false);
	};

	useEffect(() => {
		setSignedMuxPreviewUrl(null);
		if (selectedWorkout?.playback_id && selectedWorkout?.playback_id !== "null") {
			setPreviewVideo(selectedWorkout?.playback_id);
		}
	}, [selectedWorkout]);

	const setPreviewVideo = async (playbackId) => {
		try {
			const videoUrlResponse = await Ajax.call(ajaxRoutes.GET_MUX_VIDEO_URL(playbackId));
			const res = await Ajax.call(videoUrlResponse.url);
			if (res) {
				setSignedMuxPreviewUrl(videoUrlResponse.url);
			} else {
				await handleClickNewVideo();
				setAddingNewVideo(true);
			}
		} catch (error) {
			console.error("error: ", error);
		}
	};

	const getMuxSignedUrl = async () => {
		try {
			const { data } = await getMuxSignedUrlTrigger();
			setMuxDirectUploadUrl(data.url);
			setMuxDirectUploadId(data.id);
		} catch (error) {
			console.error("error", error);
		}
	};

	const editWorkoutUploadId = async (uploadId) => {
		try {
			await editWorkoutTrigger({
				workout_id: selectedWorkout.id,
				gym_id: domainGymId,
				upload_id: uploadId,
			}).unwrap();
			await Promise.all([refetchWorkouts(), refetchCategories()]);
		} catch (error) {
			console.error("error", error);
		}
	};

	const handleClickNewVideo = async () => {
		try {
			setSignedMuxPreviewUrl(null);
			setMuxDirectUploadUrl(null);
			setMuxDirectUploadId(null);
			setAddingNewVideo(true);
			form.setFieldValue("status", workoutStatuses.DRAFT);
			await refetchWorkouts();
			getMuxSignedUrl();
		} catch (error) {
			console.error("error", error);
		}
	};

	useEffect(() => {
		if (selectedWorkout) {
			if (!selectedWorkout?.playback_id) {
				setAddingNewVideo(true);
				getMuxSignedUrl();
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedWorkout?.playback_id]);

	const sortedCategories = React.useMemo(
		() =>
			sortByProperty(categoriesData?.categories || [], "name")?.map((cat) => ({
				value: cat.name,
				id: cat.id,
				label: cat.name,
			})),
		[categoriesData?.categories]
	);

	const sortedCoaches = React.useMemo(
		() =>
			sortByProperty(coachesData?.coaches || [], "name")?.map((coach) => ({
				label: `${decodeString(coach?.name)}`,
				value: coach?.id,
				disabled: coach?.status !== "active",
			})) || [],
		[coachesData?.coaches]
	);

	const sortedEquipments = React.useMemo(
		() =>
			sortByProperty(equipments?.equipments || [], "name").map((cat) => ({
				label: cat.name,
				id: cat.id,
				value: cat.id,
				disabled: cat.status !== "active",
			})),
		[equipments?.equipments]
	);
	const onChangeTitle = (e) => {
		const value = e.target?.value;
		const error = validateRequiredEncodedLength(value, 255);
		setError(error || "");
	};

	const afterUpload = () => {
		setUploading(false);
		setAddingNewVideo(false);
	};

	const onUploadStart = async (e) => {
		setUploading(true);
		await editWorkoutUploadId(muxDirectUploadId);
		const file = e?.detail?.file;
		if (!file || !file?.type?.startsWith("video/")) {
			notification.error({ message: "Invalid file type" });
			setUploading(false);
			return false;
		}
		return true;
	};

	const [status, setStatus] = useState(selectedWorkout?.status);
	if (!selectedWorkout) return null;
	const stream = data?.find((stream) => stream.workout_id === selectedWorkout.id);
	const isScheduled =
		selectedWorkout.type === workoutTypes.SCHEDULED_ON_DEMAND &&
		selectedWorkout.start_time &&
		dayjs(selectedWorkout.start_time).isAfter(dayjs());
	const statusColorMatch = {
		[workoutStatuses.ACTIVE]: "#b9e0ff",
		[workoutStatuses.DRAFT]: "#FFF",
		[workoutStatuses.INACTIVE]: "#ffdfc9",
	};

	const statusBorderColorMatch = {
		[workoutStatuses.ACTIVE]: "#1777ff",
		[workoutStatuses.DRAFT]: "#d9d9d9",
		[workoutStatuses.INACTIVE]: "#ffc3a1",
	};

	const onChangeStatus = (_, { value }) => {
		const isDraft = value === workoutStatuses.DRAFT;
		if (isDraft && isScheduledWorkout) {
			modal.confirm({
				content: "Changing the workout to Draft will cancel the workout release schedule.",
				okText: "Confirm",
				cancelText: "Go Back",
				title: "Confirm",
				icon: null,
				width: 520,
				maskClosable: false,
				closable: true,
				onCancel: () => form.setFieldValue("workoutStatus", selectedWorkout.status),
				onOk: () => {
					setIsScheduledWorkout(false);
					form.setFieldValue("workoutDate", null);
					form.setFieldValue("workoutTime", null);
					form.setFieldValue("workoutTimezone", defaultTimezone);
					onCancelScheduledReleased();
					setStatus(value);
				},
			});

			return;
		}
		setStatus(value);
	};

	const isScheduledPreRecorded = selectedWorkout.type === workoutTypes.SCHEDULED_PRE_RECORDED;
	return (
		<>
			<Modal
				title="Confirm"
				open={confirmOpen}
				onOk={onDeleteWorkout}
				onCancel={() => setConfirmOpen(false)}
				okText="Yes"
				cancelText="Go Back"
				maskClosable={false}
				destroyOnClose
				footer={
					<div className="sb-confirm-form__actions">
						<AntButton onClick={() => setConfirmOpen(false)} data-id="btnBack">
							Go Back
						</AntButton>
						<AntButton onClick={onDeleteWorkout} type="primary" data-id="btnConfirm">
							Yes
						</AntButton>
					</div>
				}
				data-id="workoutDeleteConfirmModal"
			>
				Are you sure you want to delete this workout?
			</Modal>
			<Form
				onKeyPress={(e) => {
					if (e.key === "Enter") {
						//Makes sure text field can still add new lines
						if (e?.target?.type !== "textarea") {
							e.preventDefault();
						}
					}
				}}
				form={form}
				onFinish={onFinish}
				layout="vertical"
				className="sb-edit-workout-form__form"
				initialValues={{
					workoutTitle: decodeString(selectedWorkout.name),
					workoutDescription: decodeString(selectedWorkout.description),
					workoutShortDescription: decodeString(selectedWorkout.short_description),
					workoutCoach: selectedWorkout.coaches?.map((c) => c.id) || [],
					workoutIsFree: selectedWorkout.is_free ? true : false,
					workoutPreviouslyLive: selectedWorkout.is_previously_live ? true : false,
					workoutCategories: selectedWorkout.categories ? selectedWorkout.categories.map((c) => c.name.toString()) : [],
					workoutEquipment: selectedWorkout.equipment_list?.map(({ id }) => id) || [],
					workoutDate: selectedWorkout?.start_time ? dayjs.tz(selectedWorkout.start_time) : null,
					workoutTime: selectedWorkout?.start_time ? dayjs.tz(selectedWorkout?.start_time).format("HH:mm") : undefined,
					workoutTimezone:
						isEditingLive && liveStream
							? liveStream.time_zone
							: selectedWorkout.is_scheduled
							? selectedWorkout.time_zone || defaultTimezone
							: defaultTimezone,
					workoutDuration:
						isEditingLive && liveStream
							? durationOptions.find(({ value }) => selectedWorkout.duration === value)?.value
							: null,
					workoutStatus: selectedWorkout.status,
					asset_end_time: dayjs(
						dayjs.duration(selectedWorkout.asset_end_time, "seconds").format("HH:mm:ss"),
						"HH:mm:ss"
					),
					asset_start_time: dayjs(
						dayjs.duration(selectedWorkout.asset_start_time, "seconds").format("HH:mm:ss"),
						"HH:mm:ss"
					),
				}}
			>
				<Typography.Title level={3} style={{ color: "#1f1f1f", textAlign: "center" }}>
					{isEditingLive ? "Edit Live Stream" : "Edit Workout"}
				</Typography.Title>
				<Typography.Paragraph style={{ textAlign: "center", marginBottom: 16, color: "#1f1f1f" }}>
					Update Workout information Below
				</Typography.Paragraph>
				<Flex justify="center" style={{ marginBottom: 16 }} justifyContent="center">
					<Flex wrap style={{ maxWidth: 575 }} gap={8} justify="center" justifyContent="center">
						{(!isEditingLive || selectedWorkout.type === workoutTypes.SCHEDULED_PRE_RECORDED) && (
							<Flex
								vertical
								flex={1}
								align="center"
								justify="space-between"
								className={
									!addingNewVideo || !(muxDirectUploadUrl && (addingNewVideo || !signedMuxPreviewUrl))
										? "sb-edit-workout-upload"
										: ""
								}
								style={{ minWidth: 258, minHeight: 220 }}
							>
								<div style={{ minWidth: 10 }} />
								{/*
						Should make this a mux uploader component that loads if there is no video or if user selects something like "change video"
						*/}
								{isLoading && <Spin size="small" />}
								{signedMuxPreviewUrl && !addingNewVideo && (
									<VideoPlayer
										styleConfig={{
											objectFit: "cover",
											margin: "0 auto 20px auto",
											height: 136,
											width: 230,
										}}
										options={{
											autoplay: false,
											controls: true,
											errorDisplay: false,
											sources: [
												{
													src: signedMuxPreviewUrl,
												},
											],
										}}
									/>
								)}
								{muxDirectUploadUrl && (addingNewVideo || !signedMuxPreviewUrl) && (
									<>
										<MuxUploadAndVideoPlayer
											loading={isLoading}
											endpoint={muxDirectUploadUrl}
											onSuccess={afterUpload}
											playbackId={selectedWorkout?.playback_id}
											videoUploaded={!addingNewVideo}
											onUploadStart={onUploadStart}
											onUploadAgain={handleClickNewVideo}
											style={{ width: 258, height: 220 }}
										/>
									</>
								)}
								{!addingNewVideo && (
									<Flex justify="center">
										<Button
											style={{ borderWidth: 1 }}
											className="primary-outlined--delete"
											onClick={handleClickNewVideo}
											type="button"
											data-id="btnChangeVideo"
										>
											<EditOutlined />
											Change Workout Video
										</Button>
									</Flex>
								)}
								{videoFile === undefined && <FormErrorTag text="This field is required" />}
							</Flex>
						)}
						{!changeThumbNail && (
							<Flex vertical className="sb-edit-workout-upload" style={{ width: 258 }}>
								<img
									height={136}
									width={230}
									src={thumbnailFile ? URL.createObjectURL(thumbnailFile) : selectedWorkout.image_url}
									alt=""
								/>
								<Flex justify="center">
									<Button
										style={{ borderWidth: 1, marginTop: 16 }}
										className="primary-outlined--delete"
										onClick={() => {
											const uploader = document.getElementById("uploader");
											if (uploader) {
												return uploader.click();
											}
											setChangeThumbnail(true);
										}}
										type="button"
										data-id="btnChangeThumbnail"
									>
										<UploadOutlined />
										Change Thumbnail
									</Button>
								</Flex>
							</Flex>
						)}

						<Flex style={{ display: changeThumbNail ? "flex" : "none", width: "100%" }} align="center" vertical>
							<ImageUpload
								className="sb-edit-workout-form__upload__input"
								onSave={onSaveThumbnail}
								onCancel={() => {
									setThumbnailFile(null);
									setChangeThumbnail(false);
								}}
								type="image"
								title="Upload Thumbnail"
								containerStyles={{ width: 230, minHeight: 136, maxHeight: 136 }}
								uploaderRef={uploaderRef}
								aspectRatio={300 / 180}
								sizeLimit={{
									limit: maxImageAssetSize,
									error: imageSizeLimitError,
								}}
								id={"uploader"}
							/>
							{!thumbnailFile && <Typography.Text style={{ color: "#EB5757" }}>This field is required</Typography.Text>}
						</Flex>
					</Flex>
				</Flex>

				{isEditingLive ? (
					<>
						<Typography.Text style={{ color: "#000" }}>Broadcast Server</Typography.Text>
						<Flex justify="space-between" className="stream-settings" data-id="sectionStreamServer">
							<Typography.Text ellipsis strong style={{ color: "#000", fontWeight: 700 }}>
								rtmp://global-live.mux.com:5222/app
							</Typography.Text>
							<Typography.Text
								copyable={{
									text: "rtmp://global-live.mux.com:5222/app",
									icon: <CopyOutlined style={{ cursor: "pointer", color: primaryColor }} />,
									onCopy: () => {
										notification.success({
											message: "Copied to clipboard",
										});
									},
								}}
								style={{
									color: primaryColor,
									backgroundColor: alpha(primaryColor, 0.1),
									borderRadius: "14px",
									padding: "4px 8px",
									whiteSpace: "nowrap",
								}}
								data-id="btnCopyStreamServer"
							>
								Copy
							</Typography.Text>
						</Flex>
						<Typography.Text style={{ color: "#000" }}>Stream Key</Typography.Text>
						<Flex justify="space-between" className="stream-settings" data-id="sectionStreamKey">
							<Typography.Text strong ellipsis style={{ color: "#000", width: "80%", fontWeight: 700 }}>
								{stream?.mux_stream_key || "-"}
							</Typography.Text>
							<Typography.Text
								copyable={{
									text: stream?.mux_stream_key,
									icon: <CopyOutlined style={{ cursor: "pointer", color: primaryColor }} />,
									onCopy: () => notification.success({ message: "Copied to clipboard" }),
								}}
								style={{
									color: primaryColor,
									backgroundColor: alpha(primaryColor, 0.1),
									borderRadius: "14px",
									padding: "4px 8px",
									whiteSpace: "nowrap",
								}}
								data-id="btnCopyStreamKey"
							>
								Copy
							</Typography.Text>
						</Flex>
					</>
				) : null}
				{!isEditingLive &&
					!addingNewVideo &&
					!muxDirectUploadUrl &&
					selectedWorkout?.duration > 0 &&
					selectedWorkout.type !== workoutTypes.SCHEDULED_PRE_RECORDED && (
						<Row>
							<Col xs={24}>
								<Collapse
									items={[
										{
											key: 1,
											children: (
												<>
													<Flex justify="space-between" gap={16} wrap style={{ margin: "0 0 16px", paddingTop: 16 }}>
														<Flex flex={1} justify="space-between">
															<Typography.Text strong style={{ color: "#000", width: 180, whiteSpace: "nowrap" }}>
																{"Original Video Duration  "}
															</Typography.Text>
															<span style={{ margin: "0 24px" }}>|</span>
															<Typography.Text
																strong
																style={{ color: "#000", whiteSpace: "nowrap" }}
																data-id="lblOriginalDuration"
															>
																{dayjs.duration(selectedWorkout.duration, "seconds").format("HH:mm:ss")}
															</Typography.Text>
														</Flex>
														<Flex flex={1} justify="space-between">
															<Typography.Text strong style={{ color: "#000", width: 180, whiteSpace: "nowrap" }}>
																{"Trimmed Duration "}
															</Typography.Text>
															<span style={{ margin: "0 24px" }}>|</span>
															<Typography.Text
																strong
																style={{ color: primaryColor, whiteSpace: "nowrap" }}
																data-id="lblTrimDuration"
															>
																{dayjs
																	.duration(
																		selectedWorkout.asset_end_time - selectedWorkout.asset_start_time || 0,
																		"seconds"
																	)
																	.format("HH:mm:ss")}
															</Typography.Text>
														</Flex>
													</Flex>
													<Row className="trim-pickers" gutter={16}>
														<Col xs={24} sm={24} md={12}>
															<Form.Item
																label="Start Time"
																name="asset_start_time"
																rules={[
																	{
																		validator: (_, value) => {
																			if (!value) return Promise.resolve();

																			const endTime = form.getFieldValue("asset_end_time");
																			const durationInSeconds = selectedWorkout.duration;

																			const startTimeSeconds =
																				value.hour() * 3600 + value.minute() * 60 + value.second();
																			if (startTimeSeconds >= durationInSeconds) {
																				return Promise.reject(new Error("Start time must be less than video duration"));
																			}

																			if (endTime) {
																				const endTimeSeconds =
																					endTime.hour() * 3600 + endTime.minute() * 60 + endTime.second();
																				if (endTimeSeconds - startTimeSeconds < 1) {
																					return Promise.reject(new Error("Start time must be before end time"));
																				}
																			}

																			return Promise.resolve();
																		},
																	},
																]}
																data-id="colStartTime"
															>
																<TimePicker
																	defaultOpenValue={dayjs("00:00:00", "HH:mm:ss")}
																	style={{ width: "100%" }}
																	size="large"
																	popupClassName="trimmer"
																	showNow={false}
																	disabledTime={(current) => {
																		return {
																			disabledHours: () => {
																				const durationInSeconds = selectedWorkout.duration;
																				const maxHour = Math.floor(durationInSeconds / 3600);
																				return Array.from({ length: 24 }, (_, i) => i).filter((hour) => hour > maxHour);
																			},
																			disabledMinutes: (selectedHour) => {
																				const durationInSeconds = selectedWorkout.duration;
																				const maxMinute = Math.floor((durationInSeconds % 3600) / 60);
																				return selectedHour === Math.floor(durationInSeconds / 3600)
																					? Array.from({ length: 60 }, (_, i) => i).filter(
																							(minute) => minute > maxMinute
																					  )
																					: [];
																			},
																			disabledSeconds: (selectedHour, selectedMinute) => {
																				const durationInSeconds = selectedWorkout.duration;
																				const maxSecond = durationInSeconds % 60;
																				return selectedHour === Math.floor(durationInSeconds / 3600) &&
																					selectedMinute === Math.floor((durationInSeconds % 3600) / 60)
																					? Array.from({ length: 60 }, (_, i) => i).filter(
																							(second) => second > maxSecond
																					  )
																					: [];
																			},
																		};
																	}}
																	onChange={() => {
																		setTimeout(() => {
																			form.validateFields(["asset_end_time"]);
																		}, 0);
																	}}
																	data-id="selectStartTime"
																/>
															</Form.Item>
														</Col>
														<Col xs={24} sm={24} md={12}>
															<Form.Item
																label="End Time"
																name="asset_end_time"
																rules={[
																	{
																		validator: (_, value) => {
																			if (!value) return Promise.resolve();

																			const startTime = form.getFieldValue("asset_start_time");
																			const durationInSeconds = selectedWorkout.duration;

																			const endTimeSeconds = value.hour() * 3600 + value.minute() * 60 + value.second();
																			if (endTimeSeconds > durationInSeconds) {
																				return Promise.reject(new Error("End time cannot exceed video duration"));
																			}

																			if (startTime) {
																				const startTimeSeconds =
																					startTime.hour() * 3600 + startTime.minute() * 60 + startTime.second();
																				if (endTimeSeconds - startTimeSeconds < 1) {
																					return Promise.reject(new Error("End time must be after start time"));
																				}
																			}

																			if (endTimeSeconds === 0) {
																				return Promise.reject(new Error("End time cannot be 00:00:00"));
																			}

																			return Promise.resolve();
																		},
																	},
																]}
															>
																<TimePicker
																	defaultOpenValue={dayjs(
																		dayjs.duration(selectedWorkout.asset_end_time, "seconds").format("HH:mm:ss"),
																		"HH:mm:ss"
																	)}
																	style={{ width: "100%" }}
																	popupClassName="trimmer"
																	showNow={false}
																	size="large"
																	disabledTime={(current) => {
																		return {
																			disabledHours: () => {
																				const durationInSeconds = selectedWorkout.duration;
																				const maxHour = Math.floor(durationInSeconds / 3600);
																				return Array.from({ length: 24 }, (_, i) => i).filter((hour) => hour > maxHour);
																			},
																			disabledMinutes: (selectedHour) => {
																				const durationInSeconds = selectedWorkout.duration;
																				const maxMinute = Math.floor((durationInSeconds % 3600) / 60);
																				return selectedHour === Math.floor(durationInSeconds / 3600)
																					? Array.from({ length: 60 }, (_, i) => i).filter(
																							(minute) => minute > maxMinute
																					  )
																					: [];
																			},
																			disabledSeconds: (selectedHour, selectedMinute) => {
																				const durationInSeconds = selectedWorkout.duration;
																				const maxSecond = durationInSeconds % 60;
																				return selectedHour === Math.floor(durationInSeconds / 3600) &&
																					selectedMinute === Math.floor((durationInSeconds % 3600) / 60)
																					? Array.from({ length: 60 }, (_, i) => i).filter(
																							(second) => second > maxSecond || second === 0
																					  )
																					: [];
																			},
																		};
																	}}
																	onChange={() => {
																		setTimeout(() => {
																			form.validateFields(["asset_start_time"]);
																		}, 0);
																	}}
																	data-id="selectEndTime"
																/>
															</Form.Item>
														</Col>
													</Row>
												</>
											),
											label: (
												<Flex gap={16}>
													Trim Video
													<DownOutlined className="accordion-icon" />
												</Flex>
											),
											style: { border: "none" },
										},
									]}
									style={{ border: "none" }}
									expandIcon={() => <></>}
									defaultActiveKey={[1]}
								/>
							</Col>
						</Row>
					)}
				{!isEditingLive && !isScheduledPreRecorded && (
					<div
						style={{
							"--status-color": statusColorMatch[status || selectedWorkout?.status],
							"--border-color": statusBorderColorMatch[status || selectedWorkout?.status],
						}}
					>
						<FormSelect
							name="workoutStatus"
							label="Status"
							options={[
								{ label: "Draft", value: workoutStatuses.DRAFT },
								{
									label: "Inactive",
									value: workoutStatuses.INACTIVE,
									disabled: addingNewVideo,
								},
								{
									label: "Active",
									value: workoutStatuses.ACTIVE,
									disabled:
										isScheduled ||
										!selectedWorkout?.playback_id ||
										addingNewVideo ||
										workoutTypes.SCHEDULED_ON_DEMAND === selectedWorkout.type,
								},
							]}
							onChange={onChangeStatus}
							rules={[
								{
									validator(_, value) {
										const video = muxDirectUploadUrl || selectedWorkout.video_url || selectedWorkout.playback_id;
										const imageUrl = thumbnailFile || selectedWorkout.image_url;
										if (
											selectedWorkout.status === workoutStatuses.DRAFT &&
											value !== workoutStatuses.DRAFT &&
											(!form.getFieldValue("workoutTitle") ||
												!form.getFieldValue("workoutDescription") ||
												!imageUrl ||
												!form.getFieldValue("workoutCoach") ||
												!video)
										) {
											const missingFields = [];
											if (!form.getFieldValue("workoutTitle")) missingFields.push("Title");
											if (!form.getFieldValue("workoutDescription")) missingFields.push("Description");
											if (!imageUrl) missingFields.push("Thumbnail");
											if (!form.getFieldValue("workoutCoach")) missingFields.push("Coach");
											if (!video) missingFields.push("Video");

											return Promise.reject(
												new Error(`${missingFields.join(", ")} are required to change status from 'Draft'`)
											);
										}
										return Promise.resolve();
									},
								},
							]}
						/>
					</div>
				)}

				<Row gutter={16}>
					<Col xs={24} sm={24} md={12}>
						<Form.Item
							rules={[
								() => ({
									required: true,
									validator(_, value) {
										const validationStatus = validateRequiredEncodedLength(value, 255);
										if (validationStatus) {
											return Promise.reject(new Error(validationStatus));
										}
										return Promise.resolve();
									},
								}),
							]}
						>
							<FormInput
								name="workoutTitle"
								rules={[
									{ required: true, message: "This field is required" },
									() => ({
										validator(_, value) {
											const validationStatus = validateRequiredEncodedLength(value, 255);
											if (validationStatus) {
												return Promise.reject(new Error(validationStatus));
											}
											return Promise.resolve();
										},
									}),
								]}
								label="Workout Title"
								hasFeedback={!!error}
								onChange={onChangeTitle}
								validateStatus={error ? "error" : undefined}
								help={error}
								autoFocus
							/>
						</Form.Item>
					</Col>
					<Col xs={24} sm={24} md={12}>
						{coachesData?.coaches && (
							<FormTagSelect
								name="workoutCoach"
								options={sortedCoaches}
								label="Workout Trainer"
								placeholder="Workout Trainer"
								rules={[
									{ required: true, message: "This field is required" },
									() => ({
										required: true,
										validator(_, value) {
											if (value && Array.isArray(value)) {
												const longTags = value.filter((tag) => tag.length >= 256);
												if (longTags.length > 0) {
													return Promise.reject(new Error(`Maximum allowed characters is 255 per trainer.`));
												}
											}
											return Promise.resolve();
										},
									}),
									() => ({
										required: true,
										validator(_, value) {
											if (value && Array.isArray(value)) {
												const inactiveCoaches = coachesData?.coaches
													.filter((coach) => coach.status !== "active")
													.map((coach) => coach.name.toLowerCase());
												const invalidTags = value.filter((tag) => inactiveCoaches.includes(tag.toLowerCase()));
												if (invalidTags.length > 0) {
													return Promise.reject(
														new Error(`Select inactive trainers is not allowed. (${invalidTags.join(", ")})`)
													);
												}
											}
											return Promise.resolve();
										},
									}),
								]}
								showSearch
								loading={isCoachesLoading}
							/>
						)}
					</Col>
				</Row>
				<FormTextArea
					name="workoutShortDescription"
					label="Workout Short Description"
					className="workout-short-description"
					rules={[
						() => ({
							validator(_, value) {
								const validationStatus = validateEncodedLength(value, 100);
								if (validationStatus) {
									return Promise.reject(new Error(validationStatus));
								}
								return Promise.resolve();
							},
						}),
					]}
					rows={4}
				/>
				<FormTextArea
					name="workoutDescription"
					label="Workout Description"
					rules={[
						() => ({
							required: true,
							validator(_, value) {
								const validationStatus = validateRequiredEncodedLength(value, 2048);
								if (validationStatus) {
									return Promise.reject(new Error(validationStatus));
								}
								return Promise.resolve();
							},
						}),
					]}
					rows={4}
				/>
				<Row gutter={16}>
					<Col xs={24} sm={24} md={12}>
						{categoriesData?.categories?.length > 0 && (
							<FormTagSelect
								name="workoutCategories"
								label="Workout Categories"
								placeholder="Enter Categories"
								rules={[
									() => ({
										validator(_, value) {
											if (value && Array.isArray(value)) {
												const longTags = value.filter((tag) => tag.length >= 31);
												if (longTags.length > 0) {
													return Promise.reject(new Error(`Maximum allowed characters is 30 per category.`));
												}
											}
											return Promise.resolve();
										},
									}),
								]}
								style={{ minHeight: 48 }}
								options={sortedCategories}
							/>
						)}
					</Col>
					<Col xs={24} sm={24} md={12}>
						{isEditingLive ? (
							<FormSelect
								disabled={selectedWorkout.type === workoutTypes.CURRENTLY_LIVE}
								name="workoutDuration"
								placeholder="Workout Length"
								rules={[
									{
										required: selectedWorkout?.type !== workoutTypes?.SCHEDULED_PRE_RECORDED,
										message: "This field is required",
									},
								]}
								label={<span style={{ whiteSpace: "nowrap" }}>Workout Length</span>}
								options={durationOptions}
								showSearch={false}
								style={{ height: 48 }}
							/>
						) : (
							<FormTagSelect
								name="workoutEquipment"
								label="Workout Equipment"
								mode="tags"
								placeholder="Enter Equipment"
								rules={[
									() => ({
										validator(_, value) {
											if (value && Array.isArray(value)) {
												const longTags = value.filter((tag) => tag.length >= 256);
												if (longTags.length > 0) {
													return Promise.reject(new Error(`Maximum allowed characters is 255 per equipment.`));
												}
											}
											return Promise.resolve();
										},
									}),
									() => ({
										validator(_, value) {
											if (value && Array.isArray(value)) {
												const inactiveEquipments = equipments?.equipments
													.filter((equipment) => equipment.status !== "active")
													.map((equipment) => equipment.name.toLowerCase());
												const invalidTags = value.filter((tag) => inactiveEquipments.includes(tag.toLowerCase()));
												if (invalidTags.length > 0) {
													return Promise.reject(
														new Error(`Select inactive equipment is not allowed. (${invalidTags.join(", ")})`)
													);
												}
											}
											return Promise.resolve();
										},
									}),
								]}
								style={{ minHeight: 48 }}
								options={sortedEquipments}
							/>
						)}
					</Col>
				</Row>
				<Row gutter={16}>
					<Col xs={24}>
						<Collapse
							items={[
								{
									key: 1,
									children: (
										<Flex justify="space-between" className="stream-settings">
											<Typography.Text style={{ color: "#000" }}>{selectedWorkout?.id}</Typography.Text>
											<Typography.Text
												copyable={{
													text: selectedWorkout?.id,
													icon: <CopyOutlined style={{ cursor: "pointer", color: "#fff" }} />,
													onCopy: () => notification.success({ message: "Copied to clipboard" }),
												}}
												style={{
													color: "#fff",
													backgroundColor: primaryColor,
													borderRadius: "14px",
												}}
											>
												{}
											</Typography.Text>
										</Flex>
									),
									label: (
										<Flex gap={16}>
											Show Workout ID
											<DownOutlined className="accordion-icon" />
										</Flex>
									),
									style: { border: "none" },
								},
							]}
							style={{ border: "none" }}
							expandIcon={() => <></>}
						/>
					</Col>
				</Row>
				<Row gutter={16}>
					<Col xs={24} sm={12} md={12}>
						{isPartnerGym && (
							<FormCheckbox name="workoutIsFree" tooltipTitle="This is only for use by API Partners." label="Is Free" />
						)}
					</Col>
					<Col style={{ display: "flex", justifyContent: "flex-end" }} xs={24} sm={12} md={12}>
						{isPartnerGym && (
							<FormCheckbox
								name="workoutPreviouslyLive"
								tooltipTitle="Your workout will be marked as previously live for your members"
								label="Mark Previously Live?"
							/>
						)}
					</Col>
				</Row>
				<Row gutter={16}>
					<Col xs={24} sm={24}>
						{!active && !isDraft && !isEditingLive && selectedWorkout?.type !== workoutTypes.SCHEDULED_PRE_RECORDED && (
							<Flex style={{ height: 33 }} align="center" gap={16}>
								<div>Schedule For Later?</div>
								<Tooltip title="Your workout will be activated for your members at the selected time">
									<Switch
										checked={isScheduledWorkout}
										onChange={(checked) => {
											setIsScheduledWorkout(checked);
											if (!checked) {
												form.setFieldValue("workoutDate", null);
												form.setFieldValue("workoutTime", null);
												form.setFieldValue("workoutTimezone", defaultTimezone);
												onCancelScheduledReleased();
											}
											if (checked) {
												const workoutDate = selectedWorkout?.start_time ? dayjs.tz(selectedWorkout.start_time) : null;
												const workoutTime = selectedWorkout?.start_time
													? dayjs.tz(selectedWorkout?.start_time).format("HH:mm")
													: undefined;
												const workoutTimezone =
													isEditingLive && liveStream
														? liveStream.time_zone
														: selectedWorkout.is_scheduled
														? selectedWorkout.time_zone || defaultTimezone
														: defaultTimezone;

												form.setFieldValue("workoutDate", workoutDate);
												form.setFieldValue("workoutTime", workoutTime);
												form.setFieldValue("workoutTimezone", workoutTimezone);
												setCancellingScheduledRelease(false);
											}
										}}
									/>
								</Tooltip>
							</Flex>
						)}
					</Col>
				</Row>
				<Flex className="date-time-zone">
					{isEditingLive && (
						<FormTagSelect
							name="workoutEquipment"
							label="Workout Equipment"
							placeholder="Enter Equipment"
							mode="tags"
							style={{ minHeight: 48 }}
							rules={[
								() => ({
									validator(_, value) {
										if (value && Array.isArray(value)) {
											const longTags = value.filter((tag) => tag.length >= 256);
											if (longTags.length > 0) {
												return Promise.reject(new Error(`Maximum allowed characters is 255 per equipment.`));
											}
										}
										return Promise.resolve();
									},
								}),

								() => ({
									validator(_, value) {
										if (value && Array.isArray(value)) {
											const inactiveEquipments = equipments?.equipments
												.filter((equipment) => equipment.status !== "active")
												.map((equipment) => equipment.name.toLowerCase());
											const invalidTags = value.filter((tag) => inactiveEquipments.includes(tag.toLowerCase()));
											if (invalidTags.length > 0) {
												return Promise.reject(
													new Error(`Select inactive equipment is not allowed. (${invalidTags.join(", ")})`)
												);
											}
										}
										return Promise.resolve();
									},
								}),
							]}
							options={sortedEquipments}
						/>
					)}

					{(isEditingLive || isScheduledWorkout || selectedWorkout?.type === workoutTypes.SCHEDULED_PRE_RECORDED) && (
						<DateTimeTimezone
							form={form}
							currentDatetime={currentDatetime}
							setCurrentDatetime={setCurrentDatetime}
							disabled={cancellingScheduledRelease}
							edit={
								selectedWorkout?.start_time && dayjs.tz(selectedWorkout?.start_time).add(2, "hours").isAfter(dayjs())
							}
						/>
					)}
				</Flex>
				<Flex justify="center">
					<div style={{ maxWidth: 468 }}>
						<GenreSelector
							justify={window.innerWidth >= 498 && "center"}
							initialSelectedGenreId={selectedGenre?.id}
							setSelectedGenre={setSelectedGenre}
						/>
					</div>
				</Flex>

				{!(isEditingLive || isPreRecorded) &&
					selectedWorkout.start_time &&
					dayjs(selectedWorkout.start_time)
						.tz(selectedWorkout?.time_zone || defaultTimezone)
						.isAfter(dayjs()) && (
						<Span4 className="sb-workout-details-form__form__field__label__extra">Scheduled For Release</Span4>
					)}
				<Flex wrap flex={1} align="center" className="sb-workout-music-form__actions">
					<Tooltip title={uploading ? "Cannot delete a workout while uploading is in progress" : ""}>
						<Button
							style={{ maxWidth: "unset", whiteSpace: "nowrap", flex: 1 }}
							onClick={() => {
								if (uploading) return;
								setConfirmOpen(true);
							}}
							type="button"
							className="cancel-btn"
							data-id="btnDelete"
						>
							Delete Workout
						</Button>
					</Tooltip>

					<Button
						style={{
							maxWidth: "unset",
							margin: 0,
							whiteSpace: "nowrap",
							flex: 1,
							opacity: loading || uploading || error || changeThumbNail ? 0.6 : 1,
						}}
						loading={loading}
						disabled={loading || uploading || error || changeThumbNail}
						type="submit"
						className="sb-release-schedule-form__form__button primary-filled"
						onClick={onApply}
						data-id="btnSave"
					>
						Save Changes
					</Button>
				</Flex>
			</Form>
			{contextHolder}
		</>
	);
};

export default EditWorkoutForm;

